import { z } from 'zod';

import {
  BulkCreateFileBody,
  BulkCreateFileWithFolderBody,
  BulkDownloadRequestBody,
  FileMetadataRequest,
  PutFileFoldersBody,
  RemoveFilePathsBody,
  UploadTokenRequest,
  associatedFileListSchema,
  bulkDownloadCreatedSchema,
  bulkDownloadJobSchema,
  fileMetadataResponseSchema,
  fileResponseSchema,
  folderResponseSchema,
  removeFilePathsResponseSchema,
  uploadTokensResponseSchema,
  v2DeletedFilePageSchema,
  v2FileListPageSchema,
} from './schemas';

import { useApiClient } from '@/shared/api';

/**
 * v2 파일 신규/확장 API (N2 · N4 · N16 · N13 · E2 · E6 + assets/metadata)
 * — 2026-08-08 swagger 확정 반영. 응답은 zod parse로 검증.
 */
export const useV2FileApis = () => {
  const apiClient = useApiClient();
  const domain = 'files';

  // #region N2 — 파일 상세 (대표/연관 · 분류 · metadata · 소속 폴더 목록(folders) 내장)
  const getFileDetailAPI = async (fileId: number) =>
    fileResponseSchema.parse(
      await apiClient.get<unknown>(`${domain}/${fileId}`),
    );
  // #endregion

  // #region 연관 파일 목록 (상세의 연관 트리 · 조회 전용)
  const getFileAssetsAPI = async (fileId: number) =>
    associatedFileListSchema.parse(
      await apiClient.get<unknown>(`${domain}/${fileId}/assets`),
    );
  // #endregion

  // #region E2 — 폴더 파일 목록 (FileResponse — 대표/연관/분류 확장 필드)
  const getFilesByFolderAPI = async (
    folderId: number,
    searchParams: { pageNumber: number; pageSize: number },
  ) =>
    v2FileListPageSchema.parse(
      await apiClient.get<unknown>(
        `${domain}/folders/${folderId}`,
        searchParams,
      ),
    );
  // #endregion

  // #region N4 — 소속 폴더 다중 설정 (전체 집합 교체 · 응답 = 변경 후 소속 폴더 목록)
  const putFileFoldersAPI = async (fileId: number, body: PutFileFoldersBody) =>
    z
      .array(folderResponseSchema)
      .parse(await apiClient.put<unknown>(`${domain}/${fileId}/folders`, body));
  // #endregion

  // #region 메타데이터 수정
  const putFileMetadataAPI = async (
    fileId: number,
    body: FileMetadataRequest,
  ) =>
    fileMetadataResponseSchema.parse(
      await apiClient.put<unknown>(`${domain}/${fileId}/metadata`, body),
    );
  // #endregion

  // #region N16 — 파일 1개를 여러 경로에서 제거 (남은 경로 0개면 movedToTrash)
  const postRemoveFilePathsAPI = async (body: RemoveFilePathsBody) =>
    removeFilePathsResponseSchema.parse(
      await apiClient.post<unknown>(`${domain}/remove-paths`, body),
    );
  // #endregion

  // #region N13 — 비동기 ZIP 요청 + 작업 폴링
  const postBulkDownloadAPI = async (body: BulkDownloadRequestBody) =>
    bulkDownloadCreatedSchema.parse(
      await apiClient.post<unknown>(`${domain}/bulk-download`, body),
    );

  const getBulkDownloadJobAPI = async (jobId: string) =>
    bulkDownloadJobSchema.parse(
      await apiClient.get<unknown>(`${domain}/bulk-download/${jobId}`),
    );
  // #endregion

  // #region 업로드 커밋 — 토큰 발급 → (SAS 업로드는 훅에서) → 등록
  const postUploadTokensAPI = async (body: UploadTokenRequest) =>
    uploadTokensResponseSchema.parse(
      await apiClient.post<unknown>(`${domain}/bulk/upload-token`, body),
    );

  const postBulkCreateFilesAPI = async (body: BulkCreateFileBody) => {
    await apiClient.post(`${domain}/bulk`, body);
  };

  const postBulkCreateFilesWithFolderAPI = async (
    body: BulkCreateFileWithFolderBody,
  ) => {
    await apiClient.post(`${domain}/bulk/with-folder`, body);
  };
  // #endregion

  // #region E6 — 휴지통 파일 목록 (associatedCount 포함)
  const getDeletedFilesAPI = async (searchParams: {
    pageNumber: number;
    pageSize: number;
  }) =>
    v2DeletedFilePageSchema.parse(
      await apiClient.get<unknown>(`${domain}/deleted`, searchParams),
    );
  // #endregion

  return {
    getFileDetailAPI,
    getFileAssetsAPI,
    getFilesByFolderAPI,
    putFileFoldersAPI,
    putFileMetadataAPI,
    postRemoveFilePathsAPI,
    postBulkDownloadAPI,
    getBulkDownloadJobAPI,
    postUploadTokensAPI,
    postBulkCreateFilesAPI,
    postBulkCreateFilesWithFolderAPI,
    getDeletedFilesAPI,
  };
};
